/**
 * workspace.ts (JARVIS NAV, in-place navigation)
 *
 * The MPA-safe answer to "Jarvis takes me there WHILE I talk". A full page
 * navigation destroys the JS context and with it the live Vapi call, so while
 * a voice call is live, navigation happens in a same-origin WORKSPACE IFRAME
 * layered over the page instead of a document load. The parent document (and
 * the one Vapi engine inside it) never reloads; the target page runs complete
 * and real inside the frame, including its own shell, scoped reads and
 * deep-link handling (?account= auto-opens the subscriber slide-out).
 *
 * Contract:
 *   - ENGAGED only by the dock, only when ORBI_JARVIS_NAV is on AND a voice
 *     call is live. Every other navigation stays a normal document load, byte
 *     for byte. Kill the flag, kill the layer.
 *   - SAME-ORIGIN ONLY: routes must start with "/" and not "//" (the same
 *     guard navigateRoute always had). The iframe is sandbox-free because it
 *     is our own app at our own origin - the target page needs its full
 *     capabilities (auth reads, edge-fn calls, the slide-out).
 *   - ONE DOCK: the dock bundle inside the frame sees the workspace marker on
 *     its frameElement and does not mount (entry.tsx); the parent injects its
 *     own window.OrbiDock into the frame instead, so the target page's
 *     subject priming (lifecycle-panel opening a subscriber) lands on the
 *     PARENT dock and reaches the live agent mid-call.
 *   - HONEST URL: the address bar follows the workspace via pushState, so
 *     reload/share lands on the real page at the real route. Back/forward
 *     drive the frame while the layer is up.
 *   - COMMIT ON CALL END: when the call disconnects, the workspace commits -
 *     a real document load of the current workspace route. The MPA returns to
 *     its pristine one-document state; panel state survives through the
 *     existing sessionStorage persistence.
 *
 * No em-dashes; no internal codename in operator-facing strings.
 */

const LAYER_ID = 'jarvis-workspace';

/* SPEECH SYNC: the ACTUAL outcome of a navigation attempt, as reported by the
   router that performed (or refused) it. Anything that talks about a
   navigation - the voice agent above all - must derive its words from one of
   these, never from an assumption about what a navigation "should" have done.
   moved:true is only ever returned after the move really happened (workspace
   took the route, or a document load was issued). */
export interface NavOutcome {
  moved: boolean;
  mode: 'in-place' | 'full-load' | 'refused' | 'deferred';
  /** Present exactly when moved is false: the honest, speakable reason. */
  reason?: string;
}

interface WorkspaceState {
  container: HTMLDivElement;
  frame: HTMLIFrameElement;
  route: string;
  onPop: (e: PopStateEvent) => void;
  bridgeTimer: number | null;
}

let ws: WorkspaceState | null = null;

/* JARVIS SITUATIONAL AWARENESS: the dock registers ONE listener that fires on
   every workspace frame load (a jarvis navigation AND an in-frame link click),
   with the route now on screen. This is the hook the situation re-brief rides;
   nothing else observes it. */
let navListener: ((route: string) => void) | null = null;
export function setWorkspaceNavListener(fn: ((route: string) => void) | null): void {
  navListener = fn;
}
function notifyNav(route: string | null): void {
  if (!navListener || !route) return;
  try { navListener(route); } catch { /* listener threw; navigation is unaffected */ }
}

/** True while the workspace layer is up (navigation is happening in-frame). */
export function workspaceActive(): boolean {
  return !!ws;
}

/** The window whose context the dock should read while the layer is up (the
 *  framed page IS the operator's screen), else null. */
export function workspaceWindow(): Window | null {
  try {
    return ws?.frame?.contentWindow ?? null;
  } catch {
    return null;
  }
}

/** The route the workspace currently shows (pathname + search), else null. */
export function workspaceRoute(): string | null {
  if (!ws) return null;
  // Prefer the frame's live location: in-frame link clicks move it without
  // going through workspaceNavigate.
  try {
    const loc = ws.frame.contentWindow?.location;
    if (loc && loc.origin === window.location.origin) return loc.pathname + loc.search;
  } catch { /* cross-origin frame content never happens for our own routes */ }
  return ws.route;
}

function sameOriginRoute(route: string): boolean {
  return typeof route === 'string' && route.startsWith('/') && !route.startsWith('//');
}

/** Mirror the parent dock into the framed page so its panels prime the ONE
 *  live dock (subject-by-panel keeps working mid-call), and keep the address
 *  bar honest as the frame settles. Runs on every frame load and also early
 *  via a short poll, because pages prime the subject as soon as their data
 *  lands. */
function bridgeFrame(state: WorkspaceState) {
  const inject = () => {
    try {
      const w: any = state.frame.contentWindow;
      if (!w) return false;
      const parentDock = (window as any).OrbiDock;
      if (parentDock && w.OrbiDock !== parentDock) w.OrbiDock = parentDock;
      return !!w.document && w.document.readyState === 'complete';
    } catch {
      return false;
    }
  };
  if (state.bridgeTimer != null) window.clearInterval(state.bridgeTimer);
  state.bridgeTimer = window.setInterval(() => {
    if (!ws || ws !== state || inject()) {
      if (state.bridgeTimer != null) window.clearInterval(state.bridgeTimer);
      state.bridgeTimer = null;
    }
  }, 150);
  inject();
}

function ensureLayer(): WorkspaceState | null {
  if (ws) return ws;
  try {
    const container = document.createElement('div');
    container.id = LAYER_ID;
    container.className = 'jarvis-workspace';
    const frame = document.createElement('iframe');
    frame.className = 'jarvis-workspace-frame';
    frame.setAttribute('title', 'Workspace');
    // The marker entry.tsx reads (via frameElement) to skip mounting a second
    // dock inside the frame. Same-origin, so frameElement is readable there.
    frame.dataset.jarvisWorkspace = '1';
    container.appendChild(frame);
    document.body.appendChild(container);

    const state: WorkspaceState = {
      container,
      frame,
      route: window.location.pathname + window.location.search,
      onPop: () => {
        // Back/forward while the layer is up: drive the frame to the URL the
        // history entry names. The parent document itself never navigates.
        if (!ws) return;
        const here = window.location.pathname + window.location.search;
        if (here !== workspaceRoute()) {
          ws.route = here;
          try { ws.frame.contentWindow?.location.replace(here); }
          catch { ws.frame.src = here; }
        }
      },
      bridgeTimer: null,
    };
    frame.addEventListener('load', () => {
      if (!ws || ws !== state) return;
      bridgeFrame(state);
      // ENVELOPE RELAY (2026-08-26): while the layer is up the framed page IS
      // the operator's screen, but its ora:context events fire on the FRAME's
      // document, which the parent dock cannot hear - so a drawer opened or a
      // data refresh inside the frame never re-briefed a live call. Re-dispatch
      // each frame envelope event on the parent document; the dock's one
      // listener then reads the workspace-aware envelope as usual.
      try {
        state.frame.contentWindow?.document?.addEventListener('ora:context', () => {
          if (!ws || ws !== state) return;
          try { document.dispatchEvent(new CustomEvent('ora:context')); } catch { /* best effort */ }
        });
      } catch { /* frame not readable */ }
      // In-frame link clicks are real navigations of the frame; reflect them
      // in the address bar so reload/share stays honest.
      const live = workspaceRoute();
      if (live && live !== window.location.pathname + window.location.search) {
        try { window.history.pushState({ jarvisWorkspace: true }, '', live); } catch { /* history blocked */ }
        state.route = live;
      }
      notifyNav(live);
    });
    window.addEventListener('popstate', state.onPop);
    ws = state;
    return ws;
  } catch {
    return null;
  }
}

/**
 * Navigate in place. Returns true when the workspace took the navigation (the
 * caller must NOT fall back to a document load), false when the layer could
 * not engage (caller falls back to the normal full navigation).
 */
export function workspaceNavigate(route: string): boolean {
  if (!sameOriginRoute(route)) return false;
  // TEST HOOK (nav speech gate battery, G7d): force the layer to decline so
  // navigateRoute takes its DEFERRED branch and we can assert the honest
  // deferred outcome (navigated:false + deferred + reason) end to end. This
  // mirrors the real world where the workspace layer cannot engage (DOM append
  // fails), which is otherwise unreachable with a valid same-origin route. Only
  // the harness ever sets this flag; it is never present in prod.
  try { if ((window as any).__ORBI_FORCE_DEFER__ === true) return false; } catch { /* non-browser env */ }
  const state = ensureLayer();
  if (!state) return false;
  state.route = route;
  try {
    // replace() keeps the frame's own history flat; the PARENT history is the
    // one the operator's back button drives.
    if (state.frame.contentWindow && state.frame.src) state.frame.contentWindow.location.replace(route);
    else state.frame.src = route;
  } catch {
    state.frame.src = route;
  }
  try {
    if (route !== window.location.pathname + window.location.search) {
      window.history.pushState({ jarvisWorkspace: true }, '', route);
    }
  } catch { /* history blocked: the frame still navigates; URL just lags */ }
  return true;
}

/** Tear the layer down without navigating (call error paths, tests). */
export function dismissWorkspace(): void {
  if (!ws) return;
  const state = ws;
  ws = null;
  try { window.removeEventListener('popstate', state.onPop); } catch { /* already gone */ }
  if (state.bridgeTimer != null) window.clearInterval(state.bridgeTimer);
  try { state.container.remove(); } catch { /* already gone */ }
}

/**
 * Commit the workspace: the call is over, so the MPA returns to one pristine
 * document at the route the operator is actually looking at. A no-op when the
 * layer is not up. The full load this triggers is exactly the reload the
 * layer existed to avoid WHILE the call was live; panel state survives via
 * the dock's sessionStorage persistence.
 */
export function commitWorkspace(): void {
  if (!ws) return;
  const route = workspaceRoute();
  dismissWorkspace();
  if (route && sameOriginRoute(route) && route !== window.location.pathname + window.location.search) {
    window.location.href = route;
  } else if (route && route === window.location.pathname + window.location.search) {
    // Same route the parent already shows underneath: reload to re-sync the
    // parent document with anything the framed page changed.
    window.location.reload();
  }
}
